/* eslint-disable import/no-unused-modules */
import React from 'react';
import BlogPost from 'components/Blog/BlogPost';
import BlogGuidePost from 'components/Blog/BlogGuidePost';
import { allBlogGuides } from 'contentlayer/generated';
import { getBlurDataURL } from 'helpers/getBlurDataURL';
import { GET_BLOG_POST } from '../../helpers/graphql/singlePost';
import graphQLClient from '../../helpers/graphQLClient';
import withApollo from '../../lib/withApollo';
import { useRouter } from 'next/router';
import Header from 'components/Header/Header';
import LinearProgress from '@mui/material/LinearProgress';

// Hybrid route: git-managed MDX guides (ContentLayer) shadow the DB blog —
// a guide whose slug matches an existing DB permlink wins; everything else
// falls through to the announcement pipeline.
const PostPage = ({ post, guide, images }) => {
  const router = useRouter();

  if (router.isFallback) {
    return (
      <>
        <Header />
        <LinearProgress />
      </>
    );
  }

  if (guide) return <BlogGuidePost guide={guide} images={images} />;

  return (
    <>
      <BlogPost post={post} />
    </>
  );
};

export async function getStaticProps({ params }) {
  const { permlink } = params;

  const guide = allBlogGuides.find(g => g.slug === permlink && !g.draft);
  if (guide) {
    const images = await Promise.all(
      guide.images.map(async src => ({
        src,
        // Root-relative repo assets need an absolute URL for the blur
        // service; on the very first build after adding an asset the prod
        // URL doesn't exist yet and this cleanly falls back to no blur.
        blurDataURL: await getBlurDataURL(
          src.startsWith('/') ? `https://travelfeed.com${src}` : src,
        ),
      })),
    );
    return {
      props: { guide, images },
    };
  }

  try {
    const fetchPost = await graphQLClient(GET_BLOG_POST, {
      permlink,
    });
    if (!fetchPost || !fetchPost.blogPost || !fetchPost.blogPost._id)
      return { notFound: true, revalidate: 10 };
    return {
      props: { post: fetchPost.blogPost },
    };
  } catch {
    return { notFound: true, revalidate: 10 };
  }
}

export async function getStaticPaths() {
  // Guides are pre-rendered; DB posts build on demand. 'blocking' so
  // crawlers never see the loading skeleton.
  return {
    paths: allBlogGuides
      .filter(g => !g.draft)
      .map(g => ({ params: { permlink: g.slug } })),
    fallback: 'blocking',
  };
}

export default withApollo(PostPage);
